import { type ReactElement, useMemo } from 'react';
import { hasVisibleChartValue } from '@/features/uploads/pages/executionRunsPageCharts';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Box, Text } from '@/ui/primitives';
import { darken } from '@/ui/primitives/colorUtils';
import { useTheme } from '@/ui/primitives/hooks';
import { useStackedMetricsStatusChartSectionLayout } from './StackedMetricsStatusChartSection.layout';
import {
  MiniMetricPanels,
  StatusPieBreakdownPanel,
  StatusStackedChartPanel,
} from './StackedMetricsStatusChartSection.panels';
import {
  renderStatusChartLegendLoadingState,
  resolveStackedStatusCardIcon,
  resolveStackedStatusCardIconColor,
} from './StackedMetricsStatusChartSection.states';
import { ChartToolbarPanel } from './StatusChartPanel';
import { StatusMetricCardsGrid } from './StatusMetricCardsGrid';
import {
  type MetricsStatusChartPanelModelToolbarState,
  type MetricsStatusChartPanelDisplayModel,
  useMetricsStatusChartPanelModel,
} from './useMetricsStatusChartPanelModel';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { MetricsScope } from '@/features/metrics/metricsHelpers';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';

const darkenColor = darken as (color: string, coefficient: number) => string;
export type StackedMetricsStatusChartSectionProps<TStatus extends string> = {
  title?: string;
  searchAriaLabel: string;
  noRowsBaseText: string;
  scope: MetricsScope;
  projectId: string | null;
  availableTags: string[];
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel?: string;
  metricCards: MetricCardItem[];
  loading?: boolean;
  statusCardSkeletonCount?: number;
  selectedStatusIds?: string[];
  onStatusToggle?: (statusId: string) => void;
  toolbarState?: MetricsStatusChartPanelModelToolbarState<TStatus>;
  modelOverride?: MetricsStatusChartPanelDisplayModel;
  suppressAnimation: boolean;
  showPoints?: boolean;
};
export const StackedMetricsStatusChartSection = function StackedMetricsStatusChartSection<
  TStatus extends string,
>({
  title = 'Status',
  searchAriaLabel,
  noRowsBaseText,
  scope,
  projectId,
  availableTags,
  statusOptions,
  statusFilterLabel = 'Status',
  metricCards,
  loading = false,
  statusCardSkeletonCount,
  selectedStatusIds: _selectedStatusIds,
  onStatusToggle: _onStatusToggle,
  toolbarState,
  modelOverride,
  suppressAnimation,
  showPoints: _showPoints = false,
}: StackedMetricsStatusChartSectionProps<TStatus>): ReactElement {
  const miniMetricEmptyTitle = noRowsBaseText;
  const {
    isNarrowChartLayout,
    piePanelHeight,
    pieChartVisualHeight,
    statusChartVisualHeight,
    statusPanel,
    miniPanels,
    sectionLayout,
  } = useStackedMetricsStatusChartSectionLayout();
  const theme = useTheme();

  const internalModel = useMetricsStatusChartPanelModel<TStatus>({
    noRowsBaseText,
    scope,
    projectId,
    externalToolbarState: toolbarState,
    disabled: modelOverride !== undefined,
  });
  const effectiveToolbarState = toolbarState ?? internalModel.toolbarState;
  const model = modelOverride ?? internalModel;
  const statusChartData = model.statusChartData;
  const statusPieData = useMemo(() => {
    if (!statusChartData) return [];
    return statusChartData.series
      .map((seriesItem) => {
        const total = statusChartData.data.reduce((sum, datum) => {
          const value = datum[seriesItem.key];
          return sum + (typeof value === 'number' ? value : 0);
        }, 0);
        const explicitColor = typeof seriesItem.color === 'string' ? seriesItem.color : undefined;
        const colorKey = typeof seriesItem.colorKey === 'string' ? seriesItem.colorKey : undefined;
        const indexedColor = colorKey
          ? uiTokens.statusColors[colorKey as keyof typeof uiTokens.statusColors]
          : undefined;
        const tokenColor = typeof indexedColor === 'string' ? indexedColor : undefined;
        const baseColor = explicitColor ?? tokenColor;
        const resolvedColor =
          typeof baseColor === 'string'
            ? isLightTheme(theme)
              ? baseColor
              : darkenColor(baseColor, 0.26)
            : undefined;
        return {
          name: seriesItem.label,
          value: total,
          color: resolvedColor,
        };
      })
      .filter((entry) => entry.value > 0);
  }, [statusChartData, theme]);
  const comparisonWindowLabel = useMemo(() => {
    const { fromDate, toDate, dateQuickFilter } = effectiveToolbarState;
    if (fromDate || toDate) {
      return 'All';
    }
    if (dateQuickFilter === '7d') return 'Last 7 days';
    if (dateQuickFilter === '14d') return 'Last 14 days';
    if (dateQuickFilter === '30d') return 'Last 30 days';
    return 'Latest';
  }, [effectiveToolbarState]);
  const statusLegend =
    statusPieData.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
        }}
      >
        {statusPieData.map((entry) => (
          <Box
            key={entry.name}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
            }}
          >
            <Box
              sx={{
                width: s('md'),
                height: s('md'),
                borderRadius: s('xxs'),
                backgroundColor: entry.color ?? 'text.secondary',
                flexShrink: 0,
              }}
            />
            <Text
              role="bodyMuted"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
              }}
            >
              {entry.name}
            </Text>
          </Box>
        ))}
      </Box>
    ) : null;

  const statusChartLegend =
    statusPieData.length > 0 ? (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: isNarrowChartLayout ? 'center' : 'flex-end',
          alignItems: 'center',
          columnGap: s('lg'),
          rowGap: s('xs'),
          pr: isNarrowChartLayout ? s('none') : s('2xl'),
          minWidth: 0,
        }}
      >
        {statusPieData.map((entry) => (
          <Box
            key={entry.name}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: s('md'),
            }}
          >
            <Box
              sx={{
                width: s('md'),
                height: s('md'),
                borderRadius: s('xxs'),
                backgroundColor: entry.color ?? 'text.secondary',
                flexShrink: 0,
              }}
            />
            <Text
              role="bodyMuted"
              sx={{
                fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
              }}
            >
              {entry.name}
            </Text>
          </Box>
        ))}
      </Box>
    ) : null;
  const showChartAreaLoading = loading || model.showStatusChartSkeleton;
  const resolvedStatusChartLegend = showChartAreaLoading
    ? renderStatusChartLegendLoadingState()
    : statusChartLegend;
  const hasMetricChartValue = {
    passRate: hasVisibleChartValue(model.passRateChartData, 'passRate'),
    frequency: hasVisibleChartValue(model.frequencyChartData, 'frequency'),
    duration: hasVisibleChartValue(model.durationChartData, 'duration'),
  };
  const statusEmptyTitle = effectiveToolbarState.noRowsText;

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'hidden',
        overflowY: 'hidden',
        pb: sectionLayout.bottomGap,
      }}
    >
      <Box
        sx={{
          width: '100%',
          mb: sectionLayout.toolbarGap,
        }}
      >
        <ChartToolbarPanel<TStatus>
          title=""
          searchAriaLabel={searchAriaLabel}
          searchValue={effectiveToolbarState.search}
          panelPadding="xs"
          panelPaddingX={s('md')}
          onDebouncedSearchChange={effectiveToolbarState.setSearch}
          filterStatuses={effectiveToolbarState.selectedStatuses}
          statusOptions={statusOptions}
          onFilterStatusesChange={effectiveToolbarState.setSelectedStatuses}
          filterTags={effectiveToolbarState.selectedTags}
          onFilterTagsChange={effectiveToolbarState.setSelectedTags}
          dateQuickFilter={effectiveToolbarState.dateQuickFilter}
          onDateQuickFilterChange={effectiveToolbarState.onDateQuickFilterChange}
          fromDate={effectiveToolbarState.fromDate}
          toDate={effectiveToolbarState.toDate}
          onFromDateChange={(next) => {
            effectiveToolbarState.onFromDateChange(next ? next.format('YYYY-MM-DD') : null);
          }}
          onToDateChange={(next) => {
            effectiveToolbarState.onToDateChange(next ? next.format('YYYY-MM-DD') : null);
          }}
          availableTags={availableTags}
          statusFilterLabel={statusFilterLabel}
          headerTitlePaddingLeft={s('none')}
          headerControlsPaddingRight={s('none')}
        />
      </Box>
      <Box sx={{ mb: sectionLayout.toolbarGap }}>
        <StatusMetricCardsGrid
          metricCards={metricCards}
          loading={loading}
          skeletonCount={statusCardSkeletonCount}
          comparisonWindowLabel={comparisonWindowLabel}
          resolveStatusCardIcon={(statusId) => resolveStackedStatusCardIcon(statusId, scope)}
          resolveStatusCardIconColor={resolveStackedStatusCardIconColor}
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateAreas: {
            xs: '"pie" "status" "mini"',
            sm: '"pie status" "mini mini"',
            lg: '"pie status mini"',
          },
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'minmax(260px, 1.4fr) minmax(0, 2.8fr)',
            lg: 'minmax(220px, 1.5fr) minmax(0, 5fr) minmax(300px, 2.8fr)',
          },
          columnGap: sectionLayout.gap,
          rowGap: sectionLayout.rowGap,
          alignItems: 'start',
        }}
      >
        <Box sx={{ gridArea: 'pie', minWidth: 0 }}>
          <StatusPieBreakdownPanel
            loading={showChartAreaLoading}
            piePanelHeight={piePanelHeight}
            pieChartVisualHeight={pieChartVisualHeight}
            miniPanels={miniPanels}
            headerToChartGap={statusPanel.headerToChartGap}
            miniMetricEmptyTitle={miniMetricEmptyTitle}
            statusPieData={statusPieData}
            statusLegend={statusLegend}
          />
        </Box>
        <Box sx={{ gridArea: 'status', minWidth: 0 }}>
          <StatusStackedChartPanel<TStatus>
            title={title}
            searchAriaLabel={searchAriaLabel}
            statusOptions={statusOptions}
            statusFilterLabel={statusFilterLabel}
            availableTags={availableTags}
            loading={showChartAreaLoading}
            toolbarState={effectiveToolbarState}
            statusPanel={statusPanel}
            statusChartVisualHeight={statusChartVisualHeight}
            resolvedStatusChartLegend={resolvedStatusChartLegend}
            statusEmptyTitle={statusEmptyTitle}
            statusChartData={statusChartData}
            showStatusChartSkeleton={model.showStatusChartSkeleton}
            suppressAnimation={suppressAnimation}
          />
        </Box>
        <Box sx={{ gridArea: 'mini', minWidth: 0 }}>
          <MiniMetricPanels
            loading={showChartAreaLoading}
            model={model}
            miniPanels={miniPanels}
            miniMetricEmptyTitle={miniMetricEmptyTitle}
            scope={scope}
            hasMetricChartValue={hasMetricChartValue}
          />
        </Box>
      </Box>
    </Box>
  );
};
